import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { LogOut, BarChart3, Search, TrendingUp, MapPin, Target, ArrowLeft, RefreshCw, FileText, Flame, AlertTriangle, Mail, Radar, Crown, Bell, Building2, Gauge, Zap, UserPlus, Sparkles, Rocket, Map, Filter, ShieldOff, Star, ThermometerSun, Handshake, LayoutDashboard, Activity, PieChart, CalendarRange, Users, BookOpen, Clock, Settings, Shield, Database, Briefcase, Heart, UserSearch, FileSignature, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import { NotificationBell } from '../components/NotificationBell';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NAV_SECTIONS = [
  { label: 'Workspace', items: [
    { path: '/backoffice/intelligence/command-center', label: 'Command Center', icon: LayoutDashboard },
    { path: '/backoffice/intelligence/opportunity-priority', label: 'Opportunity Priority', icon: Crown },
    { path: '/backoffice/intelligence/signal-feeds', label: 'Signal Feeds', icon: Radar },
    { path: '/backoffice/intelligence/acquisition', label: 'Acquisition Pipeline', icon: Target },
    { path: '/backoffice/settings', label: 'Admin / Settings', icon: Settings },
    { path: '/backoffice/intelligence/operations', label: 'Agency Operations', icon: TrendingUp },
    { path: '/backoffice/intelligence/training', label: 'Training Portal', icon: BookOpen },
  ]},
  { label: 'CRM', items: [
    { path: '/backoffice/intelligence/crm', label: 'CRM Command Center', icon: Handshake },
    { path: '/backoffice/intelligence/crm?tab=companies', label: 'Companies', icon: Building2 },
    { path: '/backoffice/intelligence/crm?tab=contacts', label: 'Contacts', icon: Users },
    { path: '/backoffice/intelligence/crm?tab=pipeline', label: 'Pipeline', icon: TrendingUp },
    { path: '/backoffice/intelligence/crm?tab=agreements', label: 'Agreements', icon: FileSignature },
    { path: '/backoffice/intelligence/crm?tab=job-orders', label: 'Job Orders', icon: Briefcase },
    { path: '/backoffice/intelligence/crm?tab=revenue', label: 'Revenue', icon: DollarSign },
  ]},
];

const NAV_ITEMS = NAV_SECTIONS.flatMap(s => s.items);

export const IntelligenceLayout = ({ children, title, embedded = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (embedded) { setUser({}); return; }
    const token = localStorage.getItem('backoffice_token');
    const userData = localStorage.getItem('backoffice_user');
    if (!token || !userData) { navigate('/backoffice/login'); return; }
    setUser(JSON.parse(userData));
  }, [navigate, embedded]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const token = localStorage.getItem('backoffice_token');
      const res = await fetch(`${API_URL}/api/intelligence/ingest`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success('Data refresh started. New data will appear shortly.');
      } else {
        toast.error('Failed to start refresh');
      }
    } catch {
      toast.error('Failed to start refresh');
    }
    setTimeout(() => setRefreshing(false), 3000);
  };

  if (!user) return null;

  if (embedded) {
    return (
      <div className="flex-1 flex flex-col min-w-0" data-testid="intelligence-layout-embedded">
        {children}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f0f4f8] flex" data-testid="intelligence-layout">
      <aside className="w-56 bg-[#0A1F44] text-white flex-shrink-0 flex flex-col">
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <img src="https://customer-assets.emergentagent.com/job_talent-delivery-2/artifacts/n21hqg7x_1000003920%20%281%29.png" alt="Omnia Paratus" className="h-8" />
          <NotificationBell />
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          <Link to="/backoffice" className="flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/10 rounded-lg transition-colors">
            <ArrowLeft className="h-4 w-4" /> Dashboard
          </Link>
          {NAV_SECTIONS.map(section => (
            <div key={section.label}>
              <div className="mt-4 mb-2 px-3">
                <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{section.label}</p>
              </div>
              {section.items.map(item => {
                const [itemPath, itemQuery] = item.path.split('?');
                const isActive = location.pathname === itemPath && (!itemQuery || location.search.includes(itemQuery));
                return (
                  <Link key={item.path} to={item.path}
                    className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${
                      isActive ? 'text-white bg-[#D4AF37]/20 border border-[#D4AF37]/30' : 'text-gray-300 hover:text-white hover:bg-white/10'
                    }`}>
                    <item.icon className={`h-4 w-4 ${isActive ? 'text-[#D4AF37]' : ''}`} />
                    {item.label}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <button onClick={handleRefresh} disabled={refreshing}
            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:text-[#D4AF37] transition-colors w-full rounded-lg hover:bg-white/5">
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh Data'}
          </button>
          <button onClick={() => { localStorage.removeItem('backoffice_token'); localStorage.removeItem('backoffice_user'); navigate('/backoffice/login'); }}
            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-400 hover:text-red-400 transition-colors w-full">
            <LogOut className="h-4 w-4" /> Sign Out
          </button>
        </div>
      </aside>
      <div className="flex-1 flex flex-col min-w-0 overflow-auto">
        {children}
      </div>
    </div>
  );
};

export const useIntelligenceAuth = () => {
  const [token, setToken] = useState(null);
  useEffect(() => {
    setToken(localStorage.getItem('backoffice_token'));
  }, []);
  return token;
};

export const fetchWithAuth = async (url, options = {}) => {
  const token = localStorage.getItem('backoffice_token');
  const res = await fetch(url, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res.json();
};

export const CHART_COLORS = ['#D4AF37', '#2563eb', '#059669', '#db2777', '#7c3aed', '#d97706', '#ea580c', '#0d9488', '#dc2626', '#4f46e5'];

export const useCompanyFilters = () => {
  const [excludeStaffing, setExcludeStaffing] = useState(true);
  const [excludeRetail, setExcludeRetail] = useState(true);
  const filterParams = `&exclude_staffing=${excludeStaffing}&exclude_retail=${excludeRetail}`;
  return { excludeStaffing, setExcludeStaffing, excludeRetail, setExcludeRetail, filterParams };
};

export const CompanyFilterBar = ({ excludeStaffing, setExcludeStaffing, excludeRetail, setExcludeRetail, onApply }) => (
  <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl px-4 py-2.5 shadow-sm" data-testid="company-filter-bar">
    <Filter className="h-4 w-4 text-[#0A1F44]/50" />
    <span className="text-xs font-semibold text-[#0A1F44]/60 uppercase tracking-wider">Filters</span>
    <button
      onClick={() => { setExcludeStaffing(!excludeStaffing); onApply?.(); }}
      data-testid="filter-staffing-toggle"
      className={`px-3 py-1 text-xs font-medium rounded-full border transition-all ${
        excludeStaffing
          ? 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100'
          : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
      }`}
    >
      <ShieldOff className="h-3 w-3 inline mr-1" />
      {excludeStaffing ? 'Staffing Excluded' : 'Staffing Included'}
    </button>
    <button
      onClick={() => { setExcludeRetail(!excludeRetail); onApply?.(); }}
      data-testid="filter-retail-toggle"
      className={`px-3 py-1 text-xs font-medium rounded-full border transition-all ${
        excludeRetail
          ? 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100'
          : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
      }`}
    >
      <ShieldOff className="h-3 w-3 inline mr-1" />
      {excludeRetail ? 'Retail Excluded' : 'Retail Included'}
    </button>
  </div>
);

const COMPANY_TYPE_COLORS = {
  'Technology': 'bg-blue-100 text-blue-700',
  'Healthcare': 'bg-emerald-100 text-emerald-700',
  'Manufacturing': 'bg-gray-100 text-gray-700',
  'Professional Services': 'bg-purple-100 text-purple-700',
  'Financial Services': 'bg-cyan-100 text-cyan-700',
  'Government': 'bg-slate-100 text-slate-700',
  'Education': 'bg-amber-100 text-amber-700',
  'Logistics': 'bg-orange-100 text-orange-700',
  'Staffing / Recruiting': 'bg-red-100 text-red-700',
  'Retail': 'bg-pink-100 text-pink-700',
  'Other': 'bg-gray-100 text-gray-600',
};

export const CompanyTypeBadge = ({ type }) => {
  if (!type) return null;
  const colors = COMPANY_TYPE_COLORS[type] || COMPANY_TYPE_COLORS['Other'];
  return (
    <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ${colors}`} data-testid="company-type-badge">
      {type}
    </span>
  );
};

export const MetricCard = ({ label, value, subtext, trend, icon: Icon }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-md hover:border-[#D4AF37]/40 transition-all" data-testid={`metric-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-start justify-between mb-2">
      <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">{label}</p>
      {Icon && <Icon className="h-4 w-4 text-[#0A1F44]/40" />}
    </div>
    <p className="text-2xl font-bold text-[#0A1F44] mb-1">{value}</p>
    {subtext && <p className="text-sm text-gray-500">{subtext}</p>}
    {trend !== undefined && (
      <p className={`text-xs font-medium mt-1 ${trend >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
        {trend >= 0 ? '+' : ''}{trend}% MoM
      </p>
    )}
  </div>
);
